import React from 'react';
import { BrainCircuit, Share2, Target, Monitor, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { services } from '../data/mock';

const iconMap = {
  'brain-circuit': BrainCircuit,
  'share-2': Share2,
  'target': Target,
  'monitor': Monitor
};

const Services = () => {
  const handleConsultation = () => {
    window.open('https://wa.me/918185882299?text=Hi! I would like to know more about your services.', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Services</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Comprehensive digital marketing solutions designed to drive growth, generate leads, and establish market dominance
          </p>
        </div>

        {/* Service Details */}
        <div className="space-y-24">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isEven = index % 2 === 0;

            return (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  isEven ? '' : 'lg:grid-flow-dense'
                }`}
              >
                {/* Image */}
                <div className={`relative group ${isEven ? '' : 'lg:col-start-2'}`}>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/30">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-96 object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-gradient-to-r from-cyan-500/30 to-purple-500/30 backdrop-blur-sm rounded-xl border border-cyan-500/50">
                          {IconComponent && <IconComponent size={32} className="text-cyan-400" />}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Glow Effect */}
                  <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute inset-0 rounded-3xl shadow-2xl shadow-cyan-500/50"></div>
                  </div>
                </div>

                {/* Content */}
                <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
                  <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-10 rounded-3xl hover:border-cyan-400 transition-all duration-300">
                    <div className="mb-6">
                      <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wider">
                        Service {service.id}
                      </span>
                    </div>
                    <h2 className="text-4xl font-bold text-white mb-6">{service.title}</h2>
                    <p className="text-gray-400 text-lg leading-relaxed mb-8">
                      {service.fullDescription}
                    </p>
                    
                    <div className="space-y-4 mb-8">
                      <h3 className="text-xl font-semibold text-white mb-4">Key Benefits:</h3>
                      {service.benefits.map((benefit) => (
                        <div key={benefit} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-purple-600 flex items-center justify-center">
                              <Check size={14} className="text-white" />
                            </div>
                          </div>
                          <p className="text-gray-300">{benefit}</p>
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={handleConsultation}
                      className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-8 py-3 rounded-full font-semibold shadow-lg shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300 hover:scale-105"
                    >
                      Get Started <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Card>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Services Grid */}
        <div className="mt-32">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              More <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Comprehensive solutions for all your digital marketing needs
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['GMB Management', 'Graphic Designing', 'Video Editing', 'SEO', 'WhatsApp Marketing', 'Hospital Marketing', 'Content Strategy', 'Email Marketing'].map((service) => (
              <Card
                key={service}
                className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 p-6 rounded-2xl hover:border-purple-400 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/30 hover:scale-105 text-center"
              >
                <p className="text-white font-semibold">{service}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-32">
          <Card className="bg-gradient-to-r from-cyan-900/40 to-purple-900/40 border border-cyan-500/50 p-12 rounded-3xl backdrop-blur-sm shadow-2xl shadow-cyan-500/30 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's create a customized strategy that drives results for your business
            </p>
            <Button
              onClick={handleConsultation}
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-10 py-6 text-lg rounded-full font-semibold shadow-2xl shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300 hover:scale-105"
            >
              Get Free Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Services;
